import React, { useCallback, useState } from 'react';
import { Popover, Tag, Spin, Empty } from 'antd';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { attendanceApi } from '../../../utils/api';

const formatTime = (value) => {
  if (value == null || value === '') return '—';
  const s = String(value).trim();
  // فقط ساعت: "08:00" یا "08:00:00"
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) {
    return s.slice(0, 5);
  }
  // ISO با T: "2026-09-24T08:00:00+03:30"
  const isoMatch = s.match(/T(\d{2}:\d{2})(?::\d{2})?/);
  if (isoMatch) return isoMatch[1];
  // تاریخ+ساعت با فاصله: "2026-09-24 08:00:00"
  const spaceMatch = s.match(/\s(\d{2}:\d{2})(?::\d{2})?/);
  if (spaceMatch) return spaceMatch[1];
  // هر جای دیگری که الگوی HH:mm باشد
  const anyMatch = s.match(/(\d{2}:\d{2})(?::\d{2})?/);
  if (anyMatch) return anyMatch[1];
  return s;
};

const AuditBody = ({ detail, loading, error }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Spin size="small" />
      </div>
    );
  }
  if (error) {
    return <div className="text-xs text-red-500 custom-font">{error}</div>;
  }
  if (!detail) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="اطلاعاتی نیست" className="custom-font" />;
  }

  const adjustments = Array.isArray(detail.adjustments) ? detail.adjustments : [];
  const latest = adjustments[0] || null;

  return (
    <div className="custom-font text-xs space-y-2 min-w-[240px] max-w-[320px]">
      <div className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1">
        <InfoCircleOutlined />
        سابقه اصلاحات
      </div>
      <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700 p-2 space-y-1">
        <div>
          ساعت اولیه:{' '}
          <span className="font-medium tabular-nums">
            {formatTime(
              detail.original_clock_in_time ||
                detail.original_clock_in_time_formatted ||
                latest?.original_time_formatted,
            )}
            {detail.original_clock_out_time || detail.original_clock_out_time_formatted
              ? ` / ${formatTime(
                  detail.original_clock_out_time || detail.original_clock_out_time_formatted,
                )}`
              : ''}
          </span>
        </div>
        <div>
          ساعت فعلی:{' '}
          <span className="font-medium tabular-nums">
            {formatTime(
              detail.effective_clock_in_time ||
                detail.clock_in_time ||
                latest?.adjusted_time_formatted,
            )}
            {detail.effective_clock_out_time || detail.clock_out_time
              ? ` / ${formatTime(detail.effective_clock_out_time || detail.clock_out_time)}`
              : ''}
          </span>
        </div>
      </div>
      {adjustments.length === 0 ? (
        <div className="text-slate-500">سابقه‌ای ثبت نشده است.</div>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {adjustments.map((adj) => (
            <div
              key={adj.id}
              className="rounded-lg border border-amber-100 dark:border-amber-900/40 bg-amber-50/70 dark:bg-amber-950/20 p-2 space-y-0.5"
            >
              <div className="font-medium text-slate-700 dark:text-slate-200">
                {adj.field_name_label ||
                  (adj.field_name === 'clock_out_time' ? 'ساعت خروج' : 'ساعت ورود')}
              </div>
              <div className="tabular-nums text-slate-600 dark:text-slate-300">
                {formatTime(adj.original_time_formatted)} → {formatTime(adj.adjusted_time_formatted)}
              </div>
              {adj.changer?.name || adj.changed_by_name ? (
                <div>ثبت توسط: {adj.changer?.name || adj.changed_by_name}</div>
              ) : null}
              {adj.reason ? <div>علت: {adj.reason}</div> : null}
              {adj.jalali_created_at ? (
                <div className="text-slate-400">تاریخ: {adj.jalali_created_at}</div>
              ) : null}
              {adj.source ? (
                <Tag className="custom-font m-0 text-[10px]" color="blue">
                  {adj.source === 'regularization'
                    ? 'اصلاحیه'
                    : adj.source === 'manual_adjustment'
                      ? 'تعدیل دستی'
                      : adj.source}
                </Tag>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * نمایش ساعت مؤثر + نشان «اصلاح‌شده» با پاپ‌اور سابقه
 */
const AttendanceAdjustedTime = ({
  time,
  isAdjusted = false,
  attendanceId = null,
  field = 'clock_in', // clock_in | clock_out
  originalFormatted = null,
  adjustments = null,
  className = '',
}) => {
  const effective = formatTime(time);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  const loadAudit = useCallback(async () => {
    if (Array.isArray(adjustments) && adjustments.length > 0) {
      setDetail({
        is_adjusted: true,
        adjustments,
        original_clock_in_time_formatted:
          field === 'clock_in' ? originalFormatted : undefined,
        original_clock_out_time_formatted:
          field === 'clock_out' ? originalFormatted : undefined,
        effective_clock_in_time: field === 'clock_in' ? time : undefined,
        effective_clock_out_time: field === 'clock_out' ? time : undefined,
      });
      return;
    }
    if (attendanceId == null) {
      setDetail({
        is_adjusted: true,
        original_clock_in_time_formatted: field === 'clock_in' ? originalFormatted : null,
        original_clock_out_time_formatted: field === 'clock_out' ? originalFormatted : null,
        effective_clock_in_time: field === 'clock_in' ? time : null,
        effective_clock_out_time: field === 'clock_out' ? time : null,
        adjustments: [],
      });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await attendanceApi.getAdjustments(attendanceId);
      const payload = res?.data ?? res ?? {};
      setDetail(payload?.data && typeof payload.data === 'object' ? payload.data : payload);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'خطا در دریافت سابقه اصلاحات');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [adjustments, attendanceId, field, originalFormatted, time]);

  if (!isAdjusted) {
    return <span className={`tabular-nums ${className}`}>{effective === '—' ? '-' : effective}</span>;
  }

  return (
    <Popover
      trigger="hover"
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) loadAudit();
      }}
      content={<AuditBody detail={detail} loading={loading} error={error} />}
    >
      <span
        className={`inline-flex items-center gap-1 cursor-help tabular-nums ${className}`}
        style={{ fontFamily: 'CustomFont' }}
      >
        <span>{effective === '—' ? '-' : effective}</span>
        <Tag
          color="gold"
          className="custom-font m-0 text-[10px] leading-none px-1 py-0"
          icon={<EditOutlined />}
        >
          اصلاح‌شده
        </Tag>
      </span>
    </Popover>
  );
};

export { formatTime as formatAttendanceTime };
export default AttendanceAdjustedTime;
